/* eslint-disable react/no-array-index-key */

import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { useSwipeDetection } from '../../../hooks/useSwipeDetection';
import { useResizeDetection } from '../../../hooks/useResizeDetection';

import Button from '../Button';
import { IconChevronRight, IconChevronLeft } from '../icons/Icons';
import announce from '../../../helpers/announce';

const propTypes = {
    /** Slides (items) in the carousel. Use this prop OR slides, but NOT both */
    children: PropTypes.array,

    /** Slides (items) in the carousel. Use this prop OR children, but NOT both */
    slides: PropTypes.array,

    /** The approximate width in pixels for each slide. The component
     * will adjust the number of slides per page so that each item most closely
     * matches the desired width. Use this prop OR slidesPerPage, but NOT both  */
    slideTargetWidth: PropTypes.number,

    /** The number of slides to display per page (regardless of the overall
     * width). Use this prop OR slideTargetWidth, but NOT both */
    slidesPerPage: PropTypes.number,

    /** If true, each slide will have a left and right padding of 4px */
    hasGutters: PropTypes.bool,

    /** If true, a negative left and right margin are applied so the left edge
     * of the first slide and the right edge of the last slide will align to the
     * parent container's left and right edges.
     */
    hasNegativeMargins: PropTypes.bool,

    /** Adds additional 24px of bottom padding to the carousel <li>. Necessary when
     * displaying card style components.
     */
    hasPaddingBelow: PropTypes.bool,

    /** Additional class added to outer-most container */
    className: PropTypes.string,
};

export default function Carousel(props) {
    const {
        children,
        slides = [],
        slideTargetWidth,
        slidesPerPage = 1,
        hasGutters = false,
        className,
        hasNegativeMargins = false,
        hasPaddingBelow = false,
        ...rest
    } = props;

    const [numPerPage, setNumPerPage] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [maskWidth, setMaskWidth] = useState(0);
    const [itemWidth, setItemWidth] = useState(0);

    const items = Array.isArray(children) ? children : slides;
    const numItems = items && items.length ? items.length : 1;
    const maskRef = useRef();
    const isResizing = useResizeDetection();

    /**
     * When the windowSize changes, update pages and widths
     */
    useLayoutEffect(() => {
        let newNumPerPage = slidesPerPage;
        const newMaskWidth = maskRef.current.offsetWidth;
        const firstItemInView = currentPage * numPerPage - numPerPage + 1;

        if (slideTargetWidth) {
            // Calculate the number of items that when spaced out in the mask,
            // comes closest to the slideTargetWidth
            const numThatFit = Math.floor(maskRef.current.offsetWidth / slideTargetWidth);
            const remainderAtFit = newMaskWidth % slideTargetWidth;
            const remainderAtFitPlusOne =
                slideTargetWidth * (numThatFit + 1) - newMaskWidth;
            newNumPerPage =
                remainderAtFit < remainderAtFitPlusOne ? numThatFit : numThatFit + 1;
        }

        const newNumPages = Math.ceil(numItems / newNumPerPage);
        const newCurrentPage = Math.ceil(firstItemInView / newNumPerPage);

        setMaskWidth(newMaskWidth);
        setNumPerPage(newNumPerPage);
        setNumPages(newNumPages);
        setCurrentPage(newCurrentPage);
        setItemWidth(newMaskWidth / newNumPerPage || 0);
    }, [isResizing, currentPage, numItems, numPerPage, slideTargetWidth, slidesPerPage]);

    /**
     * When the page changes, announce the current page (and maybe alt text) to SR users
     */
    useEffect(() => {
        const currentChild = children[currentPage - 1];
        const altText = currentChild?.props?.alt;

        if (slidesPerPage === 1 && currentChild?.type === 'img' && altText) {
            announce(
                `${altText}. Page ${currentPage} of ${numPages}`,
                'polite',
                'current-page'
            );
        } else {
            announce(`Page ${currentPage} of ${numPages}`, 'polite', 'current-page');
        }
    }, [currentPage, numPages, children, slidesPerPage]);

    /**
     * Click handler for previous button
     */
    function handleLeftClick() {
        setCurrentPage((prev) => {
            return prev > 1 ? prev - 1 : 1;
        });
    }

    /**
     * Click handler for next button
     */
    function handleRightClick() {
        setCurrentPage((prev) => {
            return prev < numPages ? prev + 1 : numPages;
        });
    }

    // Change pages when user swipes left or right
    useSwipeDetection(maskRef, {
        right: handleLeftClick,
        left: handleRightClick,
    });

    const firstItemInViewIndex = currentPage * numPerPage - numPerPage;
    const lastItemInViewIndex = currentPage * numPerPage - 1;

    // Transition speed is roughly based on the width.
    const speed = Math.min(Math.max(maskWidth * 0.5, 200), 450);

    const ulStyle = {
        transform: `translateX(${(currentPage - 1) * -100}%)`,
        transitionDuration: `${speed}ms`,
    };

    const liStyle = {
        width: `${(itemWidth / maskWidth) * 100}%`,
        opacity: isResizing ? 0.5 : 1,
    };

    const liHasPaddingBelow = hasPaddingBelow ? { paddingBottom: '24px' } : '';

    return (
        <div
            data-component="Carousel"
            role="group"
            aria-label={`Carousel, ${numPages} pages with ${numItems} total items`}
            className={cx('sarsa-carousel', className, {
                'has-negative-margins': hasNegativeMargins,
            })}
            {...rest}
        >
            {numPages > 1 && (
                <div className="sarsa-carousel-button-position">
                    <Button
                        appearance="icon-circular"
                        size="md"
                        iconBeforeElement={<IconChevronLeft />}
                        aria-label="Previous page"
                        onClick={handleLeftClick}
                        isDisabled={currentPage === 1}
                    />
                </div>
            )}

            <div className="sarsa-carousel-mask" ref={maskRef}>
                <ul
                    className={cx('sarsa-carousel-list', {
                        'has-gutters': hasGutters,
                    })}
                    style={ulStyle}
                >
                    {items.map((child, index) => {
                        const existingStyle = { ...child.props.style };
                        const childStyle = {
                            opacity: isResizing ? 0 : 1,
                            ...existingStyle,
                        };
                        const isNotInView =
                            index < firstItemInViewIndex || index > lastItemInViewIndex;

                        return (
                            <li
                                data-component="CarouselItem"
                                key={index}
                                aria-setsize={numItems}
                                aria-posinset={(index + 1) * currentPage}
                                style={Object.assign({}, liStyle, liHasPaddingBelow)}
                                className={cx('sarsa-carousel-item', {
                                    'has-gutters': hasGutters,
                                })}
                            >
                                {React.cloneElement(child, {
                                    'aria-hidden': isNotInView,
                                    tabIndex: isNotInView ? -1 : null, // If child is a link/button this prevents the user from tabbing to the hidden element
                                    style: isNotInView ? childStyle : existingStyle,
                                })}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {numPages > 1 && (
                <div className="sarsa-carousel-button-position right">
                    <Button
                        appearance="icon-circular"
                        size="md"
                        iconBeforeElement={<IconChevronRight />}
                        aria-label="Next page"
                        onClick={handleRightClick}
                        isDisabled={currentPage === numPages}
                    />
                </div>
            )}
        </div>
    );
}

Carousel.propTypes = propTypes;
